// hooks/useItineraryContent.js
import { useState } from 'react';
import axios from 'axios';
import { DEFAULT_AI_MODEL } from '../constants/aiConstants';

export const useItineraryContent = (language = 'en', itineraryData, apiKey) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const generatePoiContent = async (poiData, contextInfo = '', aiModel = null) => {
    // Use the provided model or fall back to default
    const modelToUse = aiModel || DEFAULT_AI_MODEL;
    
    try {
      setError(null);
      console.log('Generating POI content with model:', modelToUse, { poiData, contextInfo });

      if (!poiData.name) {
        throw new Error('POI name is required');
      }

      const prompt = `Generate a detailed information object for the point of interest in this exact JSON format:
{
  "name": "${poiData.name}",
  "category": "${poiData.category}",
  "description": "brief engaging description",
  "inDepthDescription": "detailed historical and architectural description",
  "estimatedDuration": "number",
  "coordinates": {
    "latitude": "number",
    "longitude": "number"
  },
  "openingHours": "opening hours information",
  "entryFee": "entry fee information",
  "tips": "visitor tips and advice",
  "accessibility": "accessibility information",
  "bestTimeToVisit": "recommended visiting time",
  "historicalContext": "historical background",
  "culturalSignificance": "cultural importance",
  "hiddenGems": "lesser-known features",
  "nextStop": {
    "description": "directions to next location",
    "duration": "transit time in minutes as number",
    "transportOptions": ["array of transport methods"]
  }
}

Location Context: ${poiData.name} in ${itineraryData.metadata.city}, ${itineraryData.metadata.country}
Theme: ${itineraryData.metadata.theme}
Additional Context: ${contextInfo}`;

      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: modelToUse,
          messages: [
            {
              role: "system",
              content: "You are a tour guide expert. RESPOND ONLY WITH THE REQUESTED JSON OBJECT. Do not include any markdown, explanations, or other text. The response must be parseable by JSON.parse()."
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.7
        },
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const content = response.data?.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('No content received from API');
      }

      // Clean the content and parse JSON
      let cleanContent = content
        .replace(/```json\n?|\n?```/g, '')
        .replace(/^[#\s-]*|[#\s-]*$/g, '')
        .trim();

      try {
        return JSON.parse(cleanContent);
      } catch (parseError) {
        console.error('Raw content that failed to parse:', cleanContent);
        throw new Error(`Failed to parse API response as JSON: ${parseError.message}`);
      }
    } catch (error) {
      console.error('Error generating POI content:', error);
      throw error;
    }
  };

  const generateFullItinerary = async (contextInfo, aiModel = null) => {
    // Use the provided model or fall back to default
    const modelToUse = aiModel || DEFAULT_AI_MODEL;
    
    try {
      setIsGenerating(true);
      setError(null);
      
      console.log(`Generating full itinerary with model: ${modelToUse}`);

      const prompt = `Generate a detailed travel itinerary for ${itineraryData.metadata.city}, ${itineraryData.metadata.country} in the following exact JSON format:

{
  "description": "Overall description of the itinerary",
  "theme": "${itineraryData.metadata.theme || 'General sightseeing'}",
  "categories": ["array of relevant categories"],
  "tags": ["relevant tags"],
  "duration": "number",
  "points": [
    {
      "name": "Point of interest name",
      "category": "Museum/Historical/Cultural/etc",
      "coordinates": {
        "latitude": number,
        "longitude": number
      },
      "description": "Brief description",
      "inDepthDescription": "Detailed description with historical and cultural context",
      "estimatedDuration": number,
      
      "openingHours": "if applicable",
      "entryFee": "if applicable",
      "tips": "visitor tips",
      "accessibility": "accessibility information",
      "bestTimeToVisit": "recommended visiting time",
      "historicalContext": "historical background",
      "culturalSignificance": "cultural importance",
      "hiddenGems": "lesser-known features or nearby attractions",
      "nextStop": {
        "description": "how to get to next location",
        "duration": number,
        "transportOptions": ["available transport methods"]
      }
    }
  ]
}

Additional context: ${contextInfo}`;

      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: modelToUse,
          messages: [
            {
              role: "system",
              content: "You are a travel itinerary expert. RESPOND ONLY WITH THE REQUESTED JSON OBJECT. Follow these rules strictly:\n1. All durations must be numbers (not strings)\n2. Duration in days should be a number (e.g., 1 not '1 day')\n3. POI durations should be in minutes (e.g., 60 for 1 hour)\n4. Coordinates must be numbers\n5. Do not include any markdown or extra text\n6. The response must be parseable by JSON.parse()"
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.7
        },
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const content = response.data?.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('No content received from API');
      }

      // Clean the content of any potential markdown or extra formatting
      let cleanContent = content
        .replace(/```json\n?|\n?```/g, '') // Remove code blocks
        .replace(/^[#\s-]*|[#\s-]*$/g, '') // Remove markdown headers
        .trim();

      try {
        return JSON.parse(cleanContent);
      } catch (parseError) {
        console.error('Raw content that failed to parse:', cleanContent);
        throw new Error(`Failed to parse API response as JSON: ${parseError.message}`);
      }
    } catch (error) {
      console.error('Error generating itinerary:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generatePoiContent,
    generateFullItinerary,
    isGenerating,
    error
  };
};